import { IContextualMenuItem, Spinner, Stack } from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { emitCustomEvent } from "global/Application";
import { useApplication } from "global/Application/Context";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import ValidationItem from "views/BlueRelationship/ValidationDialog/ValidationItem";

interface IProps extends TComponentConnectedProps { }

interface IValidationResult {
    location: string;
    validator: string;
    description: string;
    path: string;
}

const DataValidationPanel: React.FunctionComponent<IProps> = (props) => {
    const [items, setItems] = useState<IValidationMessage[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);

    const { dataSource } = useApplication();

    const handleRefresh = useCallback((event?: React.MouseEvent | React.KeyboardEvent) => {
        event?.preventDefault();
        event?.stopPropagation();
        setRefresh(prev => prev + 1);
    }, []);

    const menuItems: IContextualMenuItem[] = useMemo(() => [
        {
            key: 'refresh',
            name: 'Refresh',
            icon: 'Refresh',
            onClick: handleRefresh,
        },
    ], [handleRefresh]);

    const transformValidationResult = useCallback((result: IValidationResult): IValidationMessage => ({
        title: result.location,
        type: EValidationSeverity.warn,
        description: `${result.validator}\n${result.description}\n${result.path}`,
    }), []);

    useEffect(() => {
        const fetchValidationData = async () => {
            const snapshotId = props.state.data.snapshot.bcbs_groupconfigurationsnapshotid;

            if (!snapshotId) {
                return;
            }

            setLoading(true);

            try {
                const result = await dataSource.getGenerateSnapshotValidation(snapshotId);

                if (result.length > 0) {
                    const validationMessages = result.map(transformValidationResult);
                    setItems(validationMessages);
                } else {
                    setItems([]);
                }
            } catch (error) {
                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });
                setItems([]);
            } finally {
                setLoading(false);
            }
        };

        if (items === null)
            fetchValidationData();
    }, [refresh, props.state.data.snapshot.bcbs_groupconfigurationsnapshotid, dataSource, transformValidationResult]);

    return (
        <CustomPanel
            title="Backend Validation"
            canCollapse={true}
            items={menuItems}
        >
            {loading && (
                <Spinner
                    label="Loading"
                    styles={{ root: { margin: '25px' } }}
                />
            )}

            {!loading && items && (
                <Stack styles={{ root: { marginTop: '10px' } }}>
                    {items.map((item, index) => (
                        <ValidationItem
                            key={`${item.title}-${index}`}
                            data={item}
                        />
                    ))}
                </Stack>
            )}
        </CustomPanel>
    );
};

function mapStateToProps(state: RootState) {
    return { state };
}

const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;

export default connector(DataValidationPanel);

